'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, X } from 'lucide-react';
import { getTopScorers, getFlag } from '@/lib/api-football';
import { useLanguage } from '@/contexts/LanguageContext';

interface Scorer {
  player: { id: number; name: string; photo: string; nationality: string };
  statistics: Array<{ team: { id: number; name: string; logo: string }; goals: { total: number | null; assists: number | null } }>;
}

function ScorerRow({ scorer, idx }: { scorer: Scorer; idx: number }) {
  const stats = scorer.statistics[0];
  const goals = stats?.goals.total ?? 0;
  const assists = stats?.goals.assists ?? 0;
  const teamName = stats?.team.name ?? '';
  const teamLogo = stats?.team.logo ?? '';

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors">
      <span
        className="w-5 text-xs font-bold text-center flex-shrink-0 tabular-nums"
        style={{ color: idx < 3 ? 'hsl(25 95% 60%)' : 'hsl(222 15% 45%)' }}
      >
        {idx + 1}
      </span>
      {scorer.player.photo ? (
        <img src={scorer.player.photo} alt={scorer.player.name} className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
      ) : (
        <span className="text-lg flex-shrink-0">{getFlag(scorer.player.nationality)}</span>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white truncate">{scorer.player.name}</p>
        <div className="flex items-center gap-1">
          {teamLogo && <img src={teamLogo} alt={teamName} className="w-3 h-3 object-contain" />}
          <p className="text-xs text-slate-500 truncate">{teamName}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="font-mono text-sm font-bold text-white tabular-nums">{goals}</span>
        <span className="text-slate-600">·</span>
        <span className="font-mono text-sm text-slate-400 tabular-nums">{assists}</span>
      </div>
    </div>
  );
}

export default function TopScorersWidget() {
  const [scorers, setScorers] = useState<Scorer[]>([]);
  const [allScorers, setAllScorers] = useState<Scorer[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const { t } = useLanguage();

  const fetchPreview = useCallback(async () => {
    try {
      const data = await getTopScorers(8);
      setScorers(data ?? []);
    } catch {
      setScorers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPreview();
    const interval = setInterval(fetchPreview, 120_000);
    return () => clearInterval(interval);
  }, [fetchPreview]);

  const handleViewAll = async () => {
    setModalOpen(true);
    if (allScorers.length === 0) {
      setModalLoading(true);
      try {
        const data = await getTopScorers(50);
        setAllScorers(data ?? []);
      } catch {
        setAllScorers([]);
      } finally {
        setModalLoading(false);
      }
    }
  };

  return (
    <>
      <div
        className="rounded-xl overflow-hidden h-fit"
        style={{ background: 'hsl(222 35% 11%)', border: '1px solid hsl(222 25% 16%)' }}
      >
        <div className="px-4 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
          <Trophy size={15} className="text-yellow-500" />
          <h3 className="text-sm font-semibold text-white">{t.dashboard.topScorers}</h3>
          <span className="ml-auto text-xs text-slate-500">{t.team.goals} · {t.team.assists.slice(0,3)}</span>
        </div>

        {loading ? (
          <div className="divide-y" style={{ divideColor: 'hsl(222 25% 14%)' }}>
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                <div className="w-5 h-3 rounded" style={{ background: 'hsl(222 25% 18%)' }} />
                <div className="w-8 h-8 rounded-full" style={{ background: 'hsl(222 25% 18%)' }} />
                <div className="flex-1 space-y-1">
                  <div className="h-3 rounded w-3/4" style={{ background: 'hsl(222 25% 18%)' }} />
                  <div className="h-2 rounded w-1/2" style={{ background: 'hsl(222 25% 16%)' }} />
                </div>
              </div>
            ))}
          </div>
        ) : scorers.length === 0 ? (
          <div className="px-4 py-6 text-center">
            <p className="text-slate-500 text-xs">{t.common.noData}</p>
          </div>
        ) : (
          <div className="divide-y" style={{ divideColor: 'hsl(222 25% 14%)' }}>
            {scorers.map((scorer, idx) => (
              <ScorerRow key={scorer.player.id} scorer={scorer} idx={idx} />
            ))}
          </div>
        )}

        <div className="px-4 py-3 text-center" style={{ borderTop: '1px solid hsl(222 25% 16%)' }}>
          <button
            onClick={handleViewAll}
            className="text-xs font-medium text-orange-400 hover:text-orange-300 transition-colors"
          >
            {t.dashboard.viewAll} →
          </button>
        </div>
      </div>

      {/* Full Scorers Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div
            className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col"
            style={{
              background: 'hsl(222 35% 11%)',
              border: '1px solid hsl(222 25% 16%)',
              maxHeight: '85vh',
            }}
          >
            {/* Modal Header */}
            <div className="px-5 py-4 flex items-center gap-2 flex-shrink-0" style={{ borderBottom: '1px solid hsl(222 25% 16%)' }}>
              <Trophy size={16} className="text-yellow-500" />
              <h2 className="text-base font-semibold text-white flex-1">{t.dashboard.topScorers}</h2>
              <span className="text-xs text-slate-500 mr-3">{t.team.goals} · {t.team.assists.slice(0,3)}</span>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
              >
                <X size={16} />
              </button>
            </div>

            {/* Column labels */}
            <div className="px-4 py-2 flex items-center gap-3 flex-shrink-0" style={{ borderBottom: '1px solid hsl(222 25% 14%)' }}>
              <span className="w-5 text-xs text-slate-600 text-center">#</span>
              <span className="w-7 flex-shrink-0" />
              <span className="flex-1 text-xs text-slate-500">{t.common.player}</span>
              <span className="text-xs text-slate-500 flex-shrink-0 w-16 text-right">{t.team.goals} / {t.team.assists.slice(0,3)}</span>
            </div>

            {/* Scrollable list */}
            <div className="overflow-y-auto flex-1 divide-y" style={{ divideColor: 'hsl(222 25% 14%)' }}>
              {modalLoading ? (
                <div className="flex flex-col gap-0">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 px-4 py-3 animate-pulse">
                      <div className="w-5 h-3 rounded" style={{ background: 'hsl(222 25% 18%)' }} />
                      <div className="w-7 h-7 rounded-full" style={{ background: 'hsl(222 25% 18%)' }} />
                      <div className="flex-1 space-y-1">
                        <div className="h-3 rounded w-3/4" style={{ background: 'hsl(222 25% 18%)' }} />
                        <div className="h-2 rounded w-1/2" style={{ background: 'hsl(222 25% 16%)' }} />
                      </div>
                      <div className="w-12 h-3 rounded" style={{ background: 'hsl(222 25% 18%)' }} />
                    </div>
                  ))}
                </div>
              ) : allScorers.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <p className="text-slate-500 text-sm">{t.common.noData}</p>
                </div>
              ) : (
                allScorers.map((scorer, idx) => (
                  <ScorerRow key={scorer.player.id} scorer={scorer} idx={idx} />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}